import React, { useState, useMemo, useCallback } from 'react';
import BeaconChartWithLegend from 'src/components/BeaconV2Components/BeaconChartWithLegend/BeaconChartWithLegend';
import BasePageContainer from 'src/components/CommonV2Components/BasePageContainer/BasePageContainer';
import TopEntityChartV2 from 'src/components/BeaconV2Components/TopEntityChartV2/TopEntityChartV2';
import { safeDivide } from 'src/utils/app';
import { METRICTYPE } from 'src/utils/entityDefinitions';
import { useAppSelector } from 'src/utils/Hooks';
import { useBrandEntity, useMainEntityType } from 'src/utils/Hooks/reduxSelectors';
import { saveBarChartAsDelimitedData } from 'src/utils/chartUtils';
import { HIDE_GROUP_BY_LEVEL } from 'src/components/Layout/AtlasV2PageLayout';
import NoDataPlaceHolder from 'src/components/BeaconV2Components/common/NoDataPlaceHolder/NoDataPlaceHolder';
import useAtlasV2DorisColumnData from 'src/serverProxy/useAtlasV2DorisColumnData';
import {
  DorisMetricEndPointEnum,
  DorisMetricTypeEnum
} from 'src/components/AtlasV2Components/Consts/DorisFieldNameEnum';

export const selectedIdLink = {
  brandId: 'brand',
  categoryId: 'category',
  subCategoryId: 'subcategory',
  parentBrandId: 'manufacturer'
};

const selectedIdParams = {
  brandId: 'brandIds',
  categoryId: 'categoryIds',
  subCategoryId: 'subcategoryIds',
  parentBrandId: 'manufacturerIds'
};

const AtlasV2ConversionV2TopEntityChart = () => {
  const { isBrandEntity } = useBrandEntity();
  const [selectedId, setSelectedId] = useState(isBrandEntity ? 'categoryId' : 'brandId');
  const mainEntityType = useMainEntityType();
  const legendLabel = useAppSelector((state) => state.mainTimePeriod.displayName);
  const legendComparisonLabel = useAppSelector((state) => state.comparisonTimePeriod.displayName);

  const { data: unitsSoldData, isLoading: unitsSoldLoading } = useAtlasV2DorisColumnData({
    metricType: DorisMetricTypeEnum.UnitsSold,
    fieldName: selectedIdLink[selectedId],
    queryKey: 'conversion-rate-topEntity-unitsSold',
    endPoint: DorisMetricEndPointEnum.SalesEndPoint
  });
  const selectedIdValues = unitsSoldData.map((unit) => unit.metadata[selectedId]);

  const { data: trafficData, isLoading: trafficLoading } = useAtlasV2DorisColumnData({
    metricType: DorisMetricTypeEnum.TotalClicks,
    fieldName: selectedIdLink[selectedId],
    queryKey: 'conversion-rate-topEntity-traffic',
    endPoint: DorisMetricEndPointEnum.GetTrafficByTypeEndPoint,
    additionalParams: {
      [`${selectedIdParams[selectedId]}`]: selectedIdValues
    },
    enableFetch: !unitsSoldLoading
  });

  const trafficDataMap = useMemo(() => {
    const map = {};
    trafficData.forEach((entity) => {
      map[`${entity.metadata.name}-${entity.metadata[selectedId]}`] = {
        currentTotalTraffic: entity.values?.[selectedIdLink[selectedId]],
        compareTotalTraffic: entity.comparisonValues?.[selectedIdLink[selectedId]]
      };
    });
    return map;
  }, [trafficData, selectedId]);

  const { currentConversionData, compareConversionData, conversionMetaData } = useMemo(() => {
    const currentData = [];
    const compareData = [];
    const metaData = [];

    unitsSoldData.forEach((unitsSoldDatum) => {
      const propertySelector = `${unitsSoldDatum.metadata.name}-${unitsSoldDatum.metadata[selectedId]}`;

      if (trafficDataMap[propertySelector]) {
        const currentRate = safeDivide(
          unitsSoldDatum.values[selectedIdLink[selectedId]],
          trafficDataMap[propertySelector].currentTotalTraffic
        );

        const compareRate = safeDivide(
          unitsSoldDatum.comparisonValues[selectedIdLink[selectedId]],
          trafficDataMap[propertySelector].compareTotalTraffic
        );

        currentData.push([unitsSoldDatum.metadata.name, currentRate]);
        compareData.push([unitsSoldDatum.metadata.name, compareRate]);

        metaData.push({
          name: unitsSoldDatum.metadata?.name,
          id: unitsSoldDatum.metadata[selectedId],
          type: selectedIdLink[selectedId] || ''
        });
      }
    });

    return { currentConversionData: currentData, compareConversionData: compareData, conversionMetaData: metaData };
  }, [unitsSoldData, selectedId, trafficDataMap]);

  const handleDropdownChange = useCallback((option) => setSelectedId(option.id), []);

  const dropdownOptions = useMemo(
    () =>
      [
        { id: 'brandId', label: 'Brand' },
        { id: 'categoryId', label: 'Category' },
        { id: 'subCategoryId', label: 'Subcategory' },
        { id: 'parentBrandId', label: 'Manufacturer' }
      ].filter(
        (option) =>
          !option.id.toLowerCase().startsWith(mainEntityType) &&
          HIDE_GROUP_BY_LEVEL?.[mainEntityType]?.[option.id] !== true
      ),
    [mainEntityType]
  );

  const noSeriesData = !currentConversionData?.length && !compareConversionData?.length;

  return (
    <BasePageContainer>
      <BeaconChartWithLegend
        title="Conversion Rate by Brand"
        singleDropdownProps={{
          prefix: 'Conversion Rate by',
          selectedId,
          onChange: handleDropdownChange,
          options: dropdownOptions
        }}
        primaryLegendProps={{
          displayName: legendLabel
        }}
        comparisonLegendProps={{
          displayName: legendComparisonLabel
        }}
        loading={trafficLoading || unitsSoldLoading}
        convertSeriesToDelimitedData={async () => {
          const label = `Conversion Rate`;
          return saveBarChartAsDelimitedData(currentConversionData, compareConversionData, {
            fileTitle: `${label}`,
            metricLabel: label
          });
        }}
      >
        {noSeriesData ? (
          <NoDataPlaceHolder
            title="Data currently unavailable"
            description="There is currently no data matching your selected criteria. Please try another query to view insights."
            flexProps={{ margin: '80px auto' }}
          />
        ) : (
          <TopEntityChartV2
            chartSeries={[
              {
                type: 'column',
                data: compareConversionData,
                metricType: METRICTYPE.PERCENT,
                categories: conversionMetaData
              },
              {
                type: 'column',
                data: currentConversionData,
                metricType: METRICTYPE.PERCENT
              }
            ]}
          />
        )}
      </BeaconChartWithLegend>
    </BasePageContainer>
  );
};

export default AtlasV2ConversionV2TopEntityChart;
